// --------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WidgetLocalizationKeys, LazyWidget, IconURIPath } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';
import { IsNullOrEmpty } from '@Utils/Micro';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { PortKeys, DateRangePickerData, DateRangePickerDataPortValidator, DateRangePickerAlignment } from './DateRangePickerStructs';
// --------------------------------------------------------------------------------
const DateRangePicker = React.lazy(() => import('./DateRangePickerAsync'));
const DateRangePickerSettings = React.lazy(() => import('./DateRangePickerAsync').then(module => ({ default: module.DateRangePickerSettings })));
// --------------------------------------------------------------------------------
export class DateRangePickerWidget extends IWidgetThingsClass<DateRangePickerData> {
    // --------------------------------------------------------------------------------
    Render(): React.ReactNode {
        return (
            <LazyWidget>
                <DateRangePicker />
            </LazyWidget>
        );
    }
    // --------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactNode {
        return (
            <React.Suspense fallback={<LazyLoadingAnimation />}>
                <DateRangePickerSettings
                    key={'settings-' + key}
                    NodeKey={this.NodeKey}
                    ThingKey={this.ThingKey}
                    width={width}
                    height={height}
                    WidgetData={_.cloneDeep(this.WidgetData)}
                    WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                    onWidgetDataUpdate={data => this.WidgetDataUpdate(data)}
                    SettingListToolbar={this.Parent?.SettingsListToolbar} />
            </React.Suspense>
        );
    }
    // --------------------------------------------------------------------------------
    get ports(): WebThingPort[] {
        return [
            new WebThingPort({
                Name: 'Date Range',
                PortKey: PortKeys.DateRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.DateRange, data)),
            new WebThingPort({
                Name: 'Selected Date Range',
                PortKey: PortKeys.SelectedDateRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: '',
                State: ''
            }, WebThingPortType.Trigger)
        ] as WebThingPort[];
    }
    // --------------------------------------------------------------------------------
    initThing() {
        let webThing = new WebThing(
            this.WebModule,
            WidgetDescriptionToPlegmaThing(this.Description),
            this.ports
        );
        this.ThingKey = webThing.ThingKey;
    }
    // --------------------------------------------------------------------------------
    updateThing() {
        let node = WebNode.GetInstanceKey(this.NodeKey);
        let thing = node.GetThing(this.ThingKey);

        thing.Ports = {};
        _.forEach(this.ports, p => thing.AddPort(p));

        node.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // --------------------------------------------------------------------------------
    GetDefaultWidgetData(): DateRangePickerData {
        return DateRangePickerWidget.GetDefaultWidgetDataStatic();
    }
    // --------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DateRangePickerData {
        return {
            alignment: DateRangePickerAlignment.Center,
            initialDateRangeDays: 30,
            timePicker: false,
            timePickerIncrement: 5,
            timePickerInitialRange: 0
        } as DateRangePickerData;
    }
    // --------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            MinW: 1,
            MinH: 1,
            Key: 'DateRangePickerWidget',
            Type: 'DateRangePickerWidget',
            Title: 'Date Range Picker',
            Description: '',
            Data: DateRangePickerWidget.GetDefaultWidgetDataStatic(),
            IconURI: IconURIPath + '/svg/datepicker.svg'
        };
    }
    // --------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        switch (key) {
            case PortKeys.DateRange:
                return DateRangePickerDataPortValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // --------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let dict = {};
        let widgetTitle = this.Description.WidgetTitle;

        if (!IsNullOrEmpty(widgetTitle)) {
            dict[WidgetLocalizationKeys.Title] = widgetTitle;
        }
        return dict;
    }
    // --------------------------------------------------------------------------------
}